import { useEffect } from "react";

const META_PIXEL_ID = "2696670557450289";
const META_PIXEL_SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";

/**
 * Hook to initialize and manage Meta Pixel with GDPR-compliant consent handling.
 * Only loads the pixel when marketing consent is granted.
 * 
 * @param {boolean} enabled - Whether marketing consent has been granted
 */
const useMetaPixel = (enabled) => {
    useEffect(() => {
        if (typeof window === "undefined") {
            return undefined;
        }

        // Skip during ReactSnap prerendering
        const isReactSnap =
            typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";
        if (isReactSnap) {
            window.fbq = null;
            return undefined;
        }

        // If not enabled, don't load the pixel
        if (!enabled) {
            return undefined;
        }

        // Don't re-initialize if already loaded
        if (window.fbq && window.__metaPixelInitialized) {
            return undefined;
        }

        // Initialize fbq function
        const initializeFbq = () => {
            const n = (window.fbq = function () {
                n.callMethod
                    ? n.callMethod.apply(n, arguments)
                    : n.queue.push(arguments);
            });
            if (!window._fbq) {
                window._fbq = n;
            }
            n.push = n;
            n.loaded = true;
            n.version = "2.0";
            n.queue = [];
        };

        initializeFbq();

        // Load the script
        const script = document.createElement("script");
        script.src = META_PIXEL_SCRIPT_SRC;
        script.async = true;
        script.crossOrigin = "anonymous";
        script.setAttribute("data-meta-pixel", "true");

        script.addEventListener(
            "load",
            () => {
                if (window.fbq && !window.__metaPixelInitialized) {
                    window.fbq("init", META_PIXEL_ID);
                    window.fbq("track", "PageView");
                    window.__metaPixelInitialized = true;
                    console.log("[CookieConsent] Meta Pixel initialized.");
                }
            },
            { once: true }
        );

        script.addEventListener("error", () => {
            console.warn("[CookieConsent] Failed to load Meta Pixel.");
        });

        document.head.appendChild(script);

        // Also add noscript pixel for fallback
        const noscript = document.createElement("noscript");
        noscript.id = "meta-pixel-noscript";
        noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1" alt="" />`;
        document.body.appendChild(noscript);

        return () => {
            // Cleanup script on unmount
            const existingScript = document.querySelector(
                'script[data-meta-pixel="true"]'
            );
            if (existingScript) {
                existingScript.remove();
            }
            const existingNoscript = document.getElementById("meta-pixel-noscript");
            if (existingNoscript) {
                existingNoscript.remove();
            }
        };
    }, [enabled]);
};

/**
 * Helper function to track Meta Pixel events.
 * Can be called from anywhere in the app after pixel is initialized.
 * 
 * @param {string} eventName - Standard event name (e.g., 'Lead', 'Contact', 'ViewContent')
 * @param {object} params - Optional parameters for the event
 */
export const trackMetaEvent = (eventName, params = {}) => {
    if (typeof window !== "undefined" && window.fbq) {
        window.fbq("track", eventName, params);
    }
};

/**
 * Helper function to track custom events.
 * 
 * @param {string} eventName - Custom event name
 * @param {object} params - Optional parameters for the event
 */
export const trackMetaCustomEvent = (eventName, params = {}) => {
    if (typeof window !== "undefined" && window.fbq) {
        window.fbq("trackCustom", eventName, params);
    }
};

export default useMetaPixel;
